// B1.3: locations domain methods (move-only from client.ts).
import { ApiClientCore, buildQueryString } from './http/core';

export class LocationsApi extends ApiClientCore {
  // ─── Locations ──────────────────────────────────────────────────────────────
  async getLocations(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/locations${buildQueryString(params)}`);
  }
  async getLocation(id: number): Promise<any> {
    return this.get(`/api/v1/locations/${id}`);
  }
  async getLocationHealth(id: number): Promise<any> {
    return this.get(`/api/v1/locations/${id}/health`);
  }
  async getLocationIsOpen(id: number): Promise<any> {
    return this.get(`/api/v1/locations/${id}/is-open`);
  }
  async getLocationSessions(locationId: number, params: Record<string, any> = {}): Promise<any[]> {
    return this.get(`/api/v1/locations/${locationId}/sessions${buildQueryString(params)}`);
  }
  async getLocationSession(sessionId: number): Promise<any> {
    return this.get(`/api/v1/location-sessions/${sessionId}`);
  }
  async createLocation(data: any): Promise<any> {
    return this.post('/api/v1/locations', data);
  }
  async updateLocation(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/locations/${id}`, data);
  }
  async deleteLocation(id: number): Promise<any> {
    return this.delete(`/api/v1/locations/${id}`);
  }
  async purgeReleaseSmokeLocations(): Promise<{ deleted: number; ids: number[] }> {
    return this.post('/api/v1/locations/maintenance/purge-release-smoke', {});
  }
  async permanentlyDeleteLocation(id: number): Promise<{ status: string; id: number }> {
    return this.delete(`/api/v1/locations/${id}/permanent`);
  }
  async setEmployeeAdditionalLocations(id: number, locationIds: number[]): Promise<any> {
    return this.put(`/api/v1/employees/${id}/additional-locations`, { location_ids: locationIds });
  }
  async getSupportLocations(): Promise<any[]> {
    return this.get('/api/v1/support/locations');
  }
  async getSupportCasesByLocation(locationId: number, params: Record<string, any> = {}): Promise<any[]> {
    return this.get(`/api/v1/support/locations/${locationId}/cases${buildQueryString(params)}`);
  }
  async getPlatformLocations(): Promise<any[]> {
    return this.get('/api/v1/platform/locations');
  }
  async getPlatformOrgLocations(organizationId: number): Promise<any[]> {
    return this.get(`/api/v1/platform/organizations/${organizationId}/locations`);
  }
  async getLocationTaskStats(locationId: number): Promise<any> {
    return this.get(`/api/v1/tasks/stats/location/${locationId}`);
  }
  async getChecklistsAnalyticsByLocation(params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/checklists/analytics/by-location${buildQueryString(params || {})}`);
  }
  async getConsumableStatsByLocation(locationId: number, days?: number): Promise<any> {
    return this.get(`/api/v1/consumables/stats/location/${locationId}${buildQueryString({ from_days_ago: days ?? 30 })}`);
  }
  async getConsumableLocationInventory(locationId: number, includeInactive?: boolean): Promise<any> {
    return this.get(
      `/api/v1/consumables/locations/${locationId}/inventory${buildQueryString({
        include_inactive: includeInactive ?? false,
      })}`,
    );
  }
  async getConsumableWorkflowTopLocations(
    params?: { date_from?: string; date_to?: string; limit?: number }
  ): Promise<any[]> {
    return this.get(`/api/v1/consumables/analytics/workflow/top-locations${buildQueryString(params || {})}`);
  }
  async getConsumableRequestLocationOptions(): Promise<
    Array<{ id: number; name: string; city: string; address: string }>
  > {
    return this.get(`/api/v1/consumables/request-location-options`);
  }
  async getAnalyticsLocationReports(days: number): Promise<any> {
    return this.get(`/api/v1/analytics/reports/locations${buildQueryString({ days })}`);
  }
  async getLocationCashState(locationId: number): Promise<any> {
    return this.get(`/api/v1/shift-reports/location/${locationId}/cash-state`);
  }
  async getLocationShiftReportStats(
    locationId: number,
    params: { from_date?: string; to_date?: string } = {}
  ): Promise<any> {
    return this.get(`/api/v1/shift-reports/stats/location/${locationId}${buildQueryString(params)}`);
  }
  async getCounterpartyLocationBalances(params: {
    location_id?: number;
    supplier_id?: number;
  } = {}): Promise<any> {
    return this.get(`/api/v1/counterparties/location-balances${buildQueryString(params)}`);
  }
}
